import { getIdCardError, isValidIdCard } from '../../shared/utils/idCardValidation';
import { portalFetch } from '../../shared/auth/portalSession';
import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { WEEK_DAYS } from '../types';
import { getServicesApi } from '../api/servicesApi';
import { getScheduleApi } from '../api/scheduleApi';
import { 
  createAppointmentApi, 
  getAvailableSlotsApi,
  getAvailableDatesApi,
  type AppointmentData,
  type AvailableDate
} from '../api/appointmentApi';
import { NORTH_ISRAEL_VILLAGES, type Village } from '../constants/villages';
import { APP_CONFIG } from '../constants/config';

type ServiceType = 'US' | 'Xray';
type BookingStatus = 'draft' | 'completed';

interface Service {
  id: string;
  name: string;
  availableDays: string[];
}

interface TimeSlot {
  time: string;
  isAvailable: boolean;
  intervalMinutes?: number;
}

interface BookingFormData {
  // Step 1: Patient Info
  fullName: string;
  phoneNumber: string;
  idCard: string;
  village: string;
  
  // Step 2: Provider
  provider: string;
  
  // Step 3: Service Type
  serviceType: ServiceType | null;
  
  // Step 4: Service (US only)
  serviceId: string;
  serviceName: string;
  
  // Step 5: Day
  selectedDay: string;
  selectedDayLabel: string;
  
  // Step 6: Time
  selectedTime: string;
  intervalMinutes?: number; // Store interval from selected slot
  
  // Metadata
  status: BookingStatus;
  createdAt?: string;
  appointmentId?: string;
}

interface InternalBooking {
  initialDraft?: AppointmentData;
  onDraftLoaded?: () => void;
  onDraftCompleted?: () => void;
  onAppointmentCreated?: () => void;
  userName?: string;
}

const InternalBooking: React.FC<InternalBooking> = ({ initialDraft, onDraftLoaded, onDraftCompleted, onAppointmentCreated, userName }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<BookingFormData>({
    fullName: '',
    phoneNumber: '',
    idCard: '',
    village: '',
    provider: '',
    serviceType: 'US',
    serviceId: '',
    serviceName: '',
    selectedDay: '',
    selectedDayLabel: '',
    selectedTime: '',
    status: 'draft',
  });
  const [draftId, setDraftId] = useState<string | undefined>(undefined);

  const [filteredVillages, setFilteredVillages] = useState<Village[]>([]);
  const [showVillageDropdown, setShowVillageDropdown] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [availableDates, setAvailableDates] = useState<AvailableDate[]>([]);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalClinicName, setOriginalClinicName] = useState<string | undefined>(undefined);
  const [duplicateAppointment, setDuplicateAppointment] = useState<AppointmentData | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const [idCardTouched, setIdCardTouched] = useState(false);
  const idCardError = getIdCardError(formData.idCard);
  const visibleIdCardError = idCardTouched ? idCardError : null;

  const totalSteps = 5;

  // Load draft on mount if provided
  useEffect(() => {
    if (initialDraft) {
      loadDraft(initialDraft);
      onDraftLoaded?.();
    }
  }, []);

  const loadDraft = (draft: AppointmentData) => {
    setIdCardTouched(false);
    console.log('Loading draft:', draft);
    setDraftId(draft.appointmentId);
    
    // Store original clinic info
    setOriginalClinicName(draft.clinicName);
    console.log('Original clinic name:', draft.clinicName);
    
    setFormData({
      fullName: draft.fullName,
      phoneNumber: draft.phoneNumber,
      idCard: draft.idCard || '',
      village: draft.village,
      provider: draft.provider || '',
      serviceType: 'US',
      serviceId: draft.serviceId || '',
      serviceName: draft.serviceName || '',
      selectedDay: draft.selectedDay || '',
      selectedDayLabel: draft.selectedDayLabel || '',
      selectedTime: draft.selectedTime || '',
      status: 'draft',
      createdAt: draft.createdAt,
      appointmentId: draft.appointmentId,
    });

    // Determine which step to continue from
    let nextStep = 1;
    if (draft.fullName && draft.phoneNumber && draft.village && isValidIdCard(draft.idCard || '')) {
      nextStep = 2;
      if (draft.provider) {
        nextStep = 3;
        if (draft.serviceId) {
          nextStep = 4;
          if (draft.selectedDay) {
            nextStep = 5;
          }
        }
      }
    }
    setCurrentStep(nextStep);
  };

  // Village autocomplete
  useEffect(() => {
    if (formData.village) {
      const filtered = NORTH_ISRAEL_VILLAGES.filter(v =>
        v.name.includes(formData.village)
      );
      setFilteredVillages(filtered);
    } else {
      setFilteredVillages([]);
    }
  }, [formData.village]);

  // Auto-save draft after each step
  useEffect(() => {
    // Only save draft if we have minimum required fields and are past step 2
    if (currentStep > 2 && formData.fullName && formData.phoneNumber && formData.provider) {
      saveDraft();
    }
  }, [currentStep]);

  // Auto-load services when on step 3 (service selection)
  useEffect(() => {
    if (currentStep === 3 && services.length === 0) {
      loadServices();
    }
  }, [currentStep]);

  // Auto-load available dates when on step 4 (date selection)
  useEffect(() => {
    if (currentStep === 4 && formData.serviceId && availableDates.length === 0) {
      loadAvailableDates();
    }
  }, [currentStep, formData.serviceId]);

  // Auto-load time slots when on step 5 (time selection)
  useEffect(() => {
    if (currentStep === 5 && formData.selectedDay && availableSlots.length === 0) {
      loadAvailableSlots(formData.selectedDay);
    }
  }, [currentStep, formData.selectedDay]);

  const saveDraft = async () => {
    try {
      // Only save if we have at least basic info filled
      if (!formData.fullName || !formData.phoneNumber || !formData.village || !isValidIdCard(formData.idCard)) {
        return;
      }

      const appointmentData: AppointmentData = {
        appointmentId: draftId, // Include existing draft ID for updates
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        idCard: formData.idCard || '',
        village: formData.village,
        provider: formData.provider,
        clinicName: originalClinicName || APP_CONFIG.CLINIC_NAME,
        createdBy: userName || APP_CONFIG.DEFAULT_CREATED_BY,
        serviceType: APP_CONFIG.ENABLE_XRAY && formData.serviceType === 'Xray' ? 1 : 2, // Default to 2 (US)
        serviceId: formData.serviceId || '',
        serviceName: formData.serviceName || '',
        selectedDay: formData.selectedDay || '',
        selectedDayLabel: formData.selectedDayLabel || '',
        selectedTime: formData.selectedTime || '',
        status: 'draft',
      };
      
      const result = await createAppointmentApi(appointmentData);
      // Save the returned appointment ID for future updates
      if (result.appointmentId && !draftId) {
        setDraftId(result.appointmentId);
      }
    } catch (err) {
      console.error('Failed to save draft:', err);
    }
  };

  const checkForDuplicateAppointment = async (): Promise<boolean> => {
    // Only check if we have ID card and service selected
    if (!formData.idCard || !formData.serviceId) {
      return false;
    }

    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const response = await portalFetch('admin', `${API_BASE_URL}/appointments/by-idcard?idCard=${formData.idCard}&serviceId=${formData.serviceId}&futureOnly=true`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const appointments: AppointmentData[] = await response.json();
        // Filter out current draft
        const duplicate = appointments.find(apt => apt.appointmentId !== draftId);
        
        if (duplicate) {
          // Delete the draft appointment since we found a duplicate
          if (draftId) {
            try {
              const { deleteAppointmentApi } = await import('../api/appointmentApi');
              await deleteAppointmentApi(draftId);
              setDraftId(undefined);
            } catch (deleteErr) {
              console.error('Failed to delete draft:', deleteErr);
            }
          }
          
          setDuplicateAppointment(duplicate);
          setShowDuplicateModal(true);
          return true; // Duplicate found
        }
      }
    } catch (err) {
      console.error('Error checking for duplicates:', err);
    }
    
    return false; // No duplicate found
  };

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await getServicesApi();
      const activeServices = data.filter(s => s.isActive).map(s => ({
        id: s.service_id,
        name: s.name,
        availableDays: s.availableDays || [],
      }));
      setServices(activeServices);
    } catch (err) {
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableDates = async () => {
    try {
      setLoading(true);
      // When ENABLE_XRAY is false, always use US (2), otherwise use the selected service type
      const scheduleType = APP_CONFIG.ENABLE_XRAY && formData.serviceType === 'Xray' ? 1 : 2;
      
      const dates = await getAvailableDatesApi(
        scheduleType,
        scheduleType === 2 ? formData.serviceId : undefined
      );
      
      setAvailableDates(dates);
    } catch (err) {
      setError('Failed to load available dates');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async (day: string) => {
    try {
      setLoading(true);
      // When ENABLE_XRAY is false, always use US (2), otherwise use the selected service type
      const scheduleType = APP_CONFIG.ENABLE_XRAY && formData.serviceType === 'Xray' ? 1 : 2;
      
      const slots = await getAvailableSlotsApi(day, scheduleType, formData.serviceId);
      setAvailableSlots(slots);
    } catch (err) {
      setError('Failed to load available time slots');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    // Validate current step
    if (currentStep === 1) {
      setIdCardTouched(true);
      if (!formData.fullName || !formData.phoneNumber || !formData.village || !formData.idCard) {
        setError('נא למלא את כל השדות');
        return;
      }
      if (idCardError) {
        setError(idCardError);
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.provider) {
        setError('נא לבחור קופת חולים');
        return;
      }
      loadServices();
    } else if (currentStep === 3) {
      if (!formData.serviceId) {
        setError('נא לבחור שירות');
        return;
      }
      
      // Check for duplicate appointment after service selection
      setLoading(true);
      const hasDuplicate = await checkForDuplicateAppointment();
      setLoading(false);
      
      if (hasDuplicate) {
        return; // Stop here if duplicate found
      }
      
      loadAvailableDates();
    } else if (currentStep === 4) {
      if (!formData.selectedDay) {
        setError('נא לבחור יום');
        return;
      }
      loadAvailableSlots(formData.selectedDay);
    }

    setError(null);
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const isCurrentStepValid = (): boolean => {
    // Disable if loading
    if (loading) return false;
    
    switch (currentStep) {
      case 1:
        return !!(formData.fullName && formData.phoneNumber && formData.village && isValidIdCard(formData.idCard));
      case 2:
        return !!formData.provider;
      case 3:
        // Disable if no services loaded or no service selected
        return services.length > 0 && !!formData.serviceId;
      case 4:
        // Disable if no dates loaded or no day selected
        return availableDates.length > 0 && !!formData.selectedDay;
      case 5:
        // Disable if no time slots loaded or no time selected
        return availableSlots.length > 0 && !!formData.selectedTime;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!formData.selectedTime) {
      setError('נא לבחור שעה');
      return;
    }

    await proceedWithBooking();
  };

  const proceedWithBooking = async () => {
    setIdCardTouched(true);
    if (idCardError) {
      setError(idCardError);
      setCurrentStep(1);
      return;
    }
    try {
      setLoading(true);
      const appointmentData: AppointmentData = {
        appointmentId: draftId, // Include draft ID to update existing record
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        idCard: formData.idCard || '',
        village: formData.village,
        provider: formData.provider,
        clinicName: originalClinicName || APP_CONFIG.CLINIC_NAME,
        createdBy: userName || APP_CONFIG.DEFAULT_CREATED_BY,
        serviceType: APP_CONFIG.ENABLE_XRAY && formData.serviceType === 'Xray' ? 1 : 2, // Default to 2 (US)
        serviceId: formData.serviceId,
        serviceName: formData.serviceName,
        selectedDay: formData.selectedDay,
        selectedDayLabel: formData.selectedDayLabel,
        selectedTime: formData.selectedTime,
        intervalMinutes: formData.intervalMinutes || 5, // Store interval to preserve original schedule
        status: 'completed',
      };

      console.log('Submitting appointment:', appointmentData);
      await createAppointmentApi(appointmentData);
      console.log('Appointment submitted successfully');
      
      // Refresh appointments list
      if (onAppointmentCreated) {
        onAppointmentCreated();
      }
      
      const wasFromDraft = !!initialDraft;
      
      // Show success toast
      toast.success('התור נקבע בהצלחה!', {
        duration: 5000,
        position: 'top-right',
        style: {
          background: '#10b981',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '8px',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#10b981',
        },
      });
      
      if (wasFromDraft) {
        // If came from draft, call onDraftCompleted to redirect back to drafts
        onDraftCompleted?.();
      } else {
        // If direct booking, reset to beginning
        setDraftId(undefined);
        setIdCardTouched(false);
        setFormData({
          fullName: '',
          phoneNumber: '',
          idCard: '',
          village: '',
          provider: '',
          serviceType: 'US',
          serviceId: '',
          serviceName: '',
          selectedDay: '',
          selectedDayLabel: '',
          selectedTime: '',
          status: 'draft',
        });
        setCurrentStep(1);
      }
    } catch (err) {
      setError('שמירת התור נכשלה');
    } finally {
      setLoading(false);
    }
  };  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step < currentStep
                    ? 'bg-green-500 text-white'
                    : step === currentStep
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}
              >
                {step < currentStep ? '✓' : step}
              </div>
              <span className="text-xs mt-2 text-center">
                {step === 1 && 'פרטים אישיים'}
                {step === 2 && 'קופת חולים'}
                {step === 3 && 'בחירת שירות'}
                {step === 4 && 'בחירת יום'}
                {step === 5 && 'בחירת שעה'}
              </span>
            </div>
            {step < totalSteps && (
              <div
                className={`flex-1 h-1 mx-2 ${
                  step < currentStep ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-slate-800 mb-4">פרטים אישיים</h3>
              <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          תעודת זהות *
        </label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]{9}"
          autoFocus
          required
          aria-invalid={Boolean(visibleIdCardError)}
          aria-describedby="booking-id-card-error"
          onBlur={() => setIdCardTouched(true)}
          value={formData.idCard}
          onChange={(e) => {
            // Only allow numeric input and limit to 9 digits
            const value = e.target.value.replace(/\D/g, '').slice(0, 9);
            setFormData({ ...formData, idCard: value });
          }}
          onKeyPress={(e) => {
            // Prevent non-numeric characters from being typed
            if (!/[0-9]/.test(e.key)) {
              e.preventDefault();
            }
          }}
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="מספר תעודת זהות"
          dir="ltr"
          maxLength={9}
        />
        <p id="booking-id-card-error" role={visibleIdCardError ? 'alert' : undefined} className={`text-xs mt-1 ${visibleIdCardError ? 'text-red-600' : 'text-slate-500'}`}>
          {visibleIdCardError || 'יש להזין 9 ספרות בלבד'}
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          שם מלא *
        </label>
        <input
          type="text"
          value={formData.fullName}
          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="הכנס שם מלא"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          מספר טלפון *
        </label>
        <input
          type="tel"
          value={formData.phoneNumber}
          onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="05X-XXXXXXX"
        />
      </div>



      <div className="relative">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          יישוב *
        </label>
        <input
          type="text"
          value={formData.village}
          onChange={(e) => {
            setFormData({ ...formData, village: e.target.value });
            setShowVillageDropdown(true);
          }}
          onFocus={() => setShowVillageDropdown(true)}
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="התחל להקליד שם יישוב..."
        />
        
        {showVillageDropdown && filteredVillages.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filteredVillages.map((village) => (
              <div
                key={village.id}
                onClick={() => {
                  setFormData({ ...formData, village: village.name });
                  setShowVillageDropdown(false);
                }}
                className="px-4 py-3 hover:bg-indigo-50 cursor-pointer transition-colors"
              >
                {village.name}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2Provider = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-slate-800 mb-4">בחר קופת חולים</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => setFormData({ ...formData, provider: 'כללית' })}
          className={`p-6 border-2 rounded-lg transition-all ${
            formData.provider === 'כללית'
              ? 'border-indigo-600 bg-indigo-50'
              : 'border-slate-300 hover:border-indigo-400'
          }`}
        >
          <div className="text-center">
            <h4 className="text-xl font-bold text-slate-800">כללית</h4>
          </div>
        </button>

        <button
          onClick={() => setFormData({ ...formData, provider: 'מאוחדת' })}
          className={`p-6 border-2 rounded-lg transition-all ${
            formData.provider === 'מאוחדת'
              ? 'border-indigo-600 bg-indigo-50'
              : 'border-slate-300 hover:border-indigo-400'
          }`}
        >
          <div className="text-center">
            <h4 className="text-xl font-bold text-slate-800">מאוחדת</h4>
          </div>
        </button>

        <button
          onClick={() => setFormData({ ...formData, provider: 'פרטי' })}
          className={`p-6 border-2 rounded-lg transition-all ${
            formData.provider === 'פרטי'
              ? 'border-indigo-600 bg-indigo-50'
              : 'border-slate-300 hover:border-indigo-400'
          }`}
        >
          <div className="text-center">
            <h4 className="text-xl font-bold text-slate-800">פרטי</h4>
          </div>
        </button>
      </div>
    </div>
  );

  const renderStep3ServiceType = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-slate-800 mb-4">בחר סוג שירות</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => setFormData({ ...formData, serviceType: 'US' })}
          className={`p-6 border-2 rounded-lg transition-all ${
            formData.serviceType === 'US'
              ? 'border-indigo-600 bg-indigo-50'
              : 'border-slate-300 hover:border-indigo-400'
          }`}
        >
          <div className="text-center">
            <div className="text-4xl mb-3">🔊</div>
            <h4 className="text-xl font-bold text-slate-800 mb-2">אולטרסאונד</h4>
            <p className="text-sm text-slate-600">Ultrasound (US)</p>
          </div>
        </button>

        <button
          onClick={() => setFormData({ ...formData, serviceType: 'Xray' })}
          className={`p-6 border-2 rounded-lg transition-all ${
            formData.serviceType === 'Xray'
              ? 'border-indigo-600 bg-indigo-50'
              : 'border-slate-300 hover:border-indigo-400'
          }`}
        >
          <div className="text-center">
            <div className="text-4xl mb-3">📸</div>
            <h4 className="text-xl font-bold text-slate-800 mb-2">צילום רנטגן</h4>
            <p className="text-sm text-slate-600">X-Ray</p>
          </div>
        </button>
      </div>
    </div>
  );

  const renderStep4Services = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-slate-800 mb-4">בחר שירות</h3>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">טוען שירותים...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() =>
                setFormData({
                  ...formData,
                  serviceId: service.id,
                  serviceName: service.name,
                })
              }
              className={`p-4 border-2 rounded-lg text-right transition-all ${
                formData.serviceId === service.id
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-slate-300 hover:border-indigo-400'
              }`}
            >
              <h4 className="text-lg font-bold text-slate-800">{service.name}</h4>
              <p className="text-sm text-slate-600 mt-1">
                זמין בימים: {service.availableDays.map(d => WEEK_DAYS.find(w => w.id === d)?.label).join(', ')}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep4or5Days = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-slate-800 mb-4">בחר תאריך</h3>
      <p className="text-slate-600 mb-4">מציג תאריכים זמינים לחודשיים הקרובים</p>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">טוען תאריכים זמינים...</p>
        </div>
      ) : availableDates.length === 0 ? (
        <div className="text-center py-8 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800">אין תאריכים זמינים כרגע</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
          {availableDates.map((dateInfo) => (
            <button
              key={dateInfo.date}
              onClick={() =>
                setFormData({
                  ...formData,
                  selectedDay: dateInfo.date,
                  selectedDayLabel: `${dateInfo.dayName} ${new Date(dateInfo.date).toLocaleDateString('he-IL')}`,
                })
              }
              className={`p-4 border-2 rounded-lg transition-all text-right ${
                formData.selectedDay === dateInfo.date
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-slate-300 hover:border-indigo-400'
              }`}
            >
              <div className="space-y-1">
                <div className="text-lg font-bold text-slate-800">{dateInfo.dayName}</div>
                <div className="text-sm text-slate-600">
                  {new Date(dateInfo.date).toLocaleDateString('he-IL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </div>
                <div className="text-xs text-indigo-600 font-medium">
                  {dateInfo.availableSlots} תורים זמינים
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderStepTimeSlots = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-slate-800 mb-4">בחר שעה</h3>
      <p className="text-slate-600">יום נבחר: <strong>{formData.selectedDayLabel}</strong></p>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">טוען שעות זמינות...</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
          {availableSlots.map((slot) => (
            <button
              key={slot.time}
              onClick={() => slot.isAvailable && setFormData({ ...formData, selectedTime: slot.time, intervalMinutes: slot.intervalMinutes || 5 })}
              disabled={!slot.isAvailable}
              className={`p-3 border-2 rounded-lg transition-all ${
                !slot.isAvailable
                  ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                  : formData.selectedTime === slot.time
                  ? 'border-indigo-600 bg-indigo-50 text-indigo-700 font-bold'
                  : 'border-slate-300 hover:border-indigo-400 text-slate-800'
              }`}
            >
              {slot.time}
              {!slot.isAvailable && (
                <div className="text-xs text-red-500 mt-1">תפוס</div>
              )}
            </button>
          ))}
        </div>
      )}
      
      {availableSlots.filter(s => s.isAvailable).length === 0 && !loading && (
        <div className="text-center py-8 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-amber-800">אין שעות פנויות ביום זה. אנא בחר יום אחר.</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 py-8 px-4">
      <Toaster />
      
      {/* Duplicate Appointment Modal */}
      {showDuplicateModal && duplicateAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-red-600">⚠️ תור קיים</h3>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateAppointment(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 font-semibold mb-3">
                למטופל קיים כבר תור עתידי לאותו שירות:
              </p>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">שם:</span>
                  <span className="font-semibold">{duplicateAppointment.fullName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">טלפון:</span>
                  <span className="font-semibold" dir="ltr">{duplicateAppointment.phoneNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">שירות:</span>
                  <span className="font-semibold">{duplicateAppointment.serviceName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">תאריך:</span>
                  <span className="font-semibold">{duplicateAppointment.selectedDayLabel || duplicateAppointment.selectedDay}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">שעה:</span>
                  <span className="font-semibold" dir="ltr">{duplicateAppointment.selectedTime}</span>
                </div>
                {duplicateAppointment.idCard && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">ת.ז.:</span>
                    <span className="font-semibold" dir="ltr">{duplicateAppointment.idCard}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateAppointment(null);
                  // Reset form to start new appointment
                  setDraftId(undefined);
                  setIdCardTouched(false);
                  setFormData({
                    fullName: '',
                    phoneNumber: '',
                    idCard: '',
                    village: '',
                    provider: '',
                    serviceType: 'US',
                    serviceId: '',
                    serviceName: '',
                    selectedDay: '',
                    selectedDayLabel: '',
                    selectedTime: '',
                    status: 'draft',
                  });
                  setCurrentStep(1);
                  setError(null);
                }}
                className="px-8 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">קביעת תור</h1>
          <p className="text-slate-600">מרכז מני הרפואי</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {renderStepIndicator()}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {/* Step Content */}
          <div className="min-h-[400px]">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2Provider()}
            {currentStep === 3 && renderStep4Services()}
            {currentStep === 4 && renderStep4or5Days()}
            {currentStep === 5 && renderStepTimeSlots()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="px-6 py-3 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              חזור
            </button>

            {currentStep < totalSteps ? (
              <button
                onClick={handleNext}
                disabled={loading || !isCurrentStepValid()}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'טוען...' : 'המשך'}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading || !formData.selectedTime}
                className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'שומר...' : 'סיים וקבע תור'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalBooking;
